/**
 * HeroPhotoPanel — split-panel photo column for presentation heroes.
 *
 * Provider taxonomy (from backend):
 *   "themealdb" | "unsplash" | "wikimedia" → real photo, shows <img>
 *   "local_asset"                           → curated local file, shows <img>
 *   "local_gradient"                        → elegant abstract gradient, no image
 *   "none" / undefined                      → same as local_gradient
 *
 * Desktop: left column (flex: 0 0 42%, full container height).
 * Mobile: right column (width: 42%, minHeight 202px).
 */
import React, { useState } from 'react';

const _GRADIENT_PROVIDERS = new Set(['local_gradient', 'none', '', undefined]);

export function HeroPhotoPanel({
  heroImage,
  isMobile         = false,
  badge,
  accentColor      = '#3b82f6',
  accentFade       = 'rgba(59,130,246,0.15)',
  accentBdr        = 'rgba(59,130,246,0.3)',
  placeholder,       // emoji/icon shown centered when no real image (e.g. "📚")
  placeholderLabel,  // small label below the illustration
  cardBg           = '#0c1220',
  title,
}) {
  const [loaded, setLoaded] = useState(false);
  const [error,  setError]  = useState(false);

  const provider  = heroImage?.provider;
  const isGradient = _GRADIENT_PROVIDERS.has(provider) || !heroImage;

  // Pick URL: prefer desktop/mobile split; fall back to url
  const imgUrl = isGradient ? null : (
    isMobile
      ? (heroImage?.mobile_url || heroImage?.desktop_url || heroImage?.url || null)
      : (heroImage?.desktop_url || heroImage?.url || null)
  );

  const hasPhoto     = !isGradient && !!imgUrl && !error;
  const photoVisible = hasPhoto && loaded;

  const outerStyle = isMobile
    ? { flexShrink: 0, width: '42%', position: 'relative', overflow: 'hidden', minHeight: 202 }
    : { flex: '0 0 42%', height: '100%', position: 'relative', overflow: 'hidden' };

  return (
    <div style={outerStyle}>

      {/* ── Background layer: always present ─────────────────────────────── */}
      <div style={{
        position: 'absolute', inset: 0, overflow: 'hidden',
        background: `linear-gradient(135deg, #070d18 0%, #0c1528 55%, #07101c 100%)`,
      }}>
        {/* Radial accent glow — visible when no photo, subtle when photo loaded */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse 75% 55% at 52% 48%, ${accentColor}${photoVisible ? '0a' : '1c'} 0%, transparent 68%)`,
          transition: 'all 0.6s ease',
        }} />

        {/* Dot grid — only show for gradient fallback */}
        {isGradient && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `radial-gradient(circle, ${accentColor}22 1px, transparent 1px)`,
            backgroundSize: '18px 18px', backgroundPosition: '9px 9px',
          }} />
        )}

        {/* Concentric rings — depth effect behind the illustration */}
        {isGradient && (
          <>
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 80, height: 80, borderRadius: '50%',
              border: `1px solid ${accentColor}18`,
              boxShadow: `0 0 0 24px ${accentColor}08, 0 0 0 48px ${accentColor}04`,
            }} />
          </>
        )}
      </div>

      {/* ── Themed illustration — shown when no real image ─────────────────── */}
      {isGradient && placeholder && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', zIndex: 1,
          transform: 'translate(-50%, -50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          pointerEvents: 'none', userSelect: 'none',
        }}>
          <div style={{
            fontSize: isMobile ? 38 : 52,
            lineHeight: 1,
            filter: `drop-shadow(0 4px 16px ${accentColor}30) drop-shadow(0 2px 6px rgba(0,0,0,0.5))`,
          }}>
            {placeholder}
          </div>
          {placeholderLabel && (
            <div style={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em',
              textTransform: 'uppercase', marginTop: 10,
              color: accentColor, opacity: 0.7,
            }}>
              {placeholderLabel}
            </div>
          )}
        </div>
      )}

      {/* ── Real photo — fades in over background ────────────────────────── */}
      {hasPhoto && (
        <img
          src={imgUrl}
          alt={title || ''}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', display: 'block',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        />
      )}

      {/* ── Directional fade edge ─────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, zIndex: 2, pointerEvents: 'none',
        ...(isMobile
          ? { left: 0, width: '55%', background: `linear-gradient(to right, ${cardBg} 0%, ${cardBg}b0 45%, transparent 100%)` }
          : { right: 0, width: '35%', background: `linear-gradient(to right, transparent, ${cardBg})` }
        ),
      }} />

      {/* ── Badge pill ────────────────────────────────────────────────────── */}
      {badge && (
        <div style={{
          position: 'absolute', top: 10, left: 12, zIndex: 4,
          padding: '4px 12px', borderRadius: 20,
          background: photoVisible ? `${accentColor}ea` : accentFade,
          border: `1px solid ${accentBdr}`,
          fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
          color: photoVisible ? '#fff' : accentColor,
          textTransform: 'uppercase',
          backdropFilter: photoVisible ? 'blur(8px)' : 'none',
          boxShadow: photoVisible ? `0 2px 12px ${accentColor}50` : 'none',
          transition: 'all 0.3s ease',
        }}>
          {badge}
        </div>
      )}

      {/* ── Photo attribution ─────────────────────────────────────────────── */}
      {photoVisible && heroImage?.photographer && provider !== 'local_gradient' && (
        <div style={{
          position: 'absolute', bottom: 5, right: 8, zIndex: 4,
          fontSize: 9, color: 'rgba(255,255,255,0.28)', lineHeight: 1,
          pointerEvents: 'none', userSelect: 'none',
        }}>
          {provider === 'unsplash'
            ? `${heroImage.photographer} / Unsplash`
            : provider === 'wikimedia' || provider === 'local_asset'
              ? 'Wikimedia Commons'
              : heroImage.photographer || ''}
        </div>
      )}
    </div>
  );
}

export default HeroPhotoPanel;
